import React, { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { FolderCheck, Loader2, ArrowUpRight, Search } from 'lucide-react';
import { Sidebar, Topbar } from './Dashboard';
import { Input } from '../components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { auth, clientService, getStoredUser } from '../api';
import { DocItemRow } from '../components/client/DocumentsPanel';
import { useDocActions } from '../components/client/useDocActions';

const periodLabel = (key) => {
  const [y, m] = key.split('-').map(Number);
  return new Date(m === 1 ? y - 1 : y, m === 1 ? 11 : m - 2, 1).toLocaleDateString(undefined, { month: 'long', year: 'numeric' });
};

const ClientCard = ({ req, actions }) => {
  const done = req.items.filter((i) => i.status === 'received').length;
  const pct = req.items.length ? Math.round((done / req.items.length) * 100) : 100;
  return (
    <div className="rounded-xl bg-white border border-stone-200 overflow-hidden" data-testid={`documents-card-${req.clientId}`}>
      <div className="px-5 py-4 border-b border-stone-100 flex items-center gap-3">
        <div className="flex-1 min-w-0">
          <Link to={`/dashboard/clients/${req.clientId}?tab=documents`} className="font-semibold text-stone-900 hover:text-emerald-800 inline-flex items-center gap-1" data-testid={`documents-client-link-${req.clientId}`}>
            {req.clientName} <ArrowUpRight size={13}/>
          </Link>
          <div className="mt-2 h-1.5 rounded-full bg-stone-100 overflow-hidden"><div className={`h-full ${pct === 100 ? 'bg-emerald-500' : 'bg-amber-500'}`} style={{ width: `${pct}%` }}/></div>
        </div>
        <span className={`text-xs font-semibold px-2.5 py-1 rounded-full ${pct === 100 ? 'bg-emerald-50 text-emerald-800' : 'bg-amber-50 text-amber-800'}`} data-testid={`documents-progress-${req.clientId}`}>{done}/{req.items.length} received</span>
      </div>
      <div className="divide-y divide-stone-100">
        {req.items.map((it) => <DocItemRow key={it.id} item={it} busy={actions.busyItem === it.id} onToggle={actions.toggle(req)} onUpload={actions.upload(req)}/>)}
      </div>
    </div>
  );
};

export default function DocumentsPage() {
  const nav = useNavigate();
  const [user, setUser] = useState(getStoredUser());
  const [data, setData] = useState(null);
  const [period, setPeriod] = useState('');
  const [filter, setFilter] = useState('outstanding');
  const [q, setQ] = useState('');

  const load = (p = period) => clientService.documentsOverview(p || undefined).then((d) => { setData(d); setPeriod(d.period); });
  const replace = (r) => setData((d) => ({ ...d, requests: d.requests.map((x) => (x.id === r.id ? r : x)) }));
  const actions = useDocActions(replace);

  useEffect(() => {
    if (!localStorage.getItem('ad_token')) { nav('/login'); return; }
    auth.me().then(setUser).catch(() => {});
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const reqs = (data?.requests || [])
    .filter((r) => r.clientName.toLowerCase().includes(q.toLowerCase()))
    .filter((r) => filter === 'all' || (filter === 'outstanding' ? r.items.some((i) => i.status !== 'received') : r.items.every((i) => i.status === 'received')));
  const totals = (data?.requests || []).reduce((a, r) => ({ items: a.items + r.items.length, done: a.done + r.items.filter((i) => i.status === 'received').length }), { items: 0, done: 0 });

  return (
    <div className="min-h-screen bg-stone-50 flex">
      <Sidebar activeLabel="Documents"/>
      <div className="flex-1 min-w-0">
        <Topbar user={user} firm={user?.firm}/>
        <main className="p-4 lg:p-8 space-y-6">
          <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-4">
            <div>
              <h1 className="text-2xl lg:text-3xl font-serif font-semibold text-stone-900 flex items-center gap-2"><FolderCheck size={24} className="text-emerald-800"/> Documents</h1>
              <p className="mt-1 text-stone-600 text-sm" data-testid="documents-summary">
                {data ? `${data.periodLabel} documents · ${totals.done} of ${totals.items} received across ${data.requests.length} clients` : 'Monthly document checklists for every client'}
              </p>
            </div>
            {data && (
              <Select value={period} onValueChange={(v) => { setPeriod(v); load(v); }}>
                <SelectTrigger className="w-56 bg-white" data-testid="documents-period-select"><SelectValue/></SelectTrigger>
                <SelectContent>{data.periods.map((p) => <SelectItem key={p} value={p}>Documents for {periodLabel(p)}</SelectItem>)}</SelectContent>
              </Select>
            )}
          </div>
          <div className="flex flex-col sm:flex-row gap-3">
            <div className="relative flex-1 max-w-sm">
              <Search size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-stone-400"/>
              <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Filter clients…" className="pl-9 bg-white" data-testid="documents-search-input"/>
            </div>
            <div className="inline-flex rounded-lg border border-stone-200 bg-white p-1" data-testid="documents-filters">
              {[['outstanding', 'Outstanding'], ['complete', 'Complete'], ['all', 'All']].map(([k, l]) => (
                <button key={k} type="button" onClick={() => setFilter(k)} data-testid={`documents-filter-${k}`}
                  className={`px-4 py-1.5 rounded-md text-sm font-medium transition-colors ${filter === k ? 'bg-emerald-800 text-white' : 'text-stone-600 hover:text-stone-900'}`}>{l}</button>
              ))}
            </div>
          </div>
          {!data && <div className="flex items-center gap-2 text-stone-600"><Loader2 className="animate-spin" size={18}/> Loading…</div>}
          {data && reqs.length === 0 && <div className="rounded-xl bg-white border border-stone-200 p-10 text-center text-sm text-stone-500" data-testid="documents-empty">{filter === 'outstanding' ? 'Nothing outstanding. Every client has sent their documents.' : 'No clients match.'}</div>}
          <div className="grid xl:grid-cols-2 gap-5">{reqs.map((r) => <ClientCard key={r.id} req={r} actions={actions}/>)}</div>
        </main>
      </div>
    </div>
  );
}
